import React, { useState } from 'react'
import {
  useTraversal,
  useLocation,
  lightFileReader,
} from '@guillotinaweb/react-gmi'
import { Alert, Spin } from 'antd'
import { AccordionCard } from '../../../../components/accordion-card'
import { getCdnUrl } from '../../../../consts'

import '../styles.css'

const cdn = getCdnUrl()
const fallbackPackImg = `${cdn}frontend/pack_fallback.svg`
const fallbackProdImg = `${cdn}frontend/bottle_fallback.svg`
const imgBehavior = 'api.behaviors.images.IImagesBehavior'

export default function ImagesEditor() {
  const ctx = useTraversal()
  const [location, setLocation] = useLocation()
  const prod = ctx.state.context

  let opciones = Object.values(prod.opciones).map(
    (o) => `${o.anyada}_${o.referencia}`
  )
  opciones = [...new Set(opciones)] // uniqe
  opciones = opciones.sort().reverse()

  return (
    <div>
      <h2 className="title is-size-6">Images</h2>
      <Alert
        message="When changing the images they are changed directly without you having to press the save button. So be careful with the images that are uploaded."
        type="warning"
      />
      {opciones.map((o) => {
        const [anyada, ref] = o.split('_')
        // We want to the accordion open when the url hash matches the reference
        const collapsed = location.get('id_producto') !== ref

        return (
          <div id={ref} key={ref}>
            <AccordionCard
              defaultCollapsed={collapsed}
              renderCollapsed={false}
              title={
                <strong>
                  {anyada} - {ref}
                </strong>
              }
            >
              <ImageSection prod={prod} referencia={ref} />
            </AccordionCard>
          </div>
        )
      })}
    </div>
  )
}

function ImageSection({ prod, referencia }) {
  const ctx = useTraversal()
  const [uploading, setUploading] = useState({})
  const [error, setError] = useState('')
  const valid_types = ['anv800', 'rev800', 'art800', 'o4', 'o5']
  const isUploading = Object.keys(uploading).length > 0

  const [imgPaths, setImgPaths] = useState(prod[imgBehavior]?.images || {})

  async function uploadImage(file, referencia, imgType) {
    const res = await ctx.client.rest.request(
      `${ctx.path}@upload-image/${referencia}/${imgType}`,
      {
        method: 'POST',
        body: file.data,
      }
    )
    if (res.status >= 300) {
      setUploading(({ ...state }) => {
        delete state[imgType]
        return state
      })
      setError(
        (error) => error + `Error ${res.status} uploading image ${imgType}`
      )
    } else {
      const body = await res.json()
      setImgPaths((imgPaths) => ({
        ...imgPaths,
        [`${referencia}_${imgType}`]: {
          path: body.path,
        },
      }))
      setUploading(({ ...state }) => {
        delete state[imgType]
        return state
      })
    }
  }

  async function onChange(files) {
    setError('')
    const firstBatch = []
    const secondBatch = []

    // 1. Validate filename and extension
    for (const i in files) {
      const f = files[i]
      const [filename, ext] = f.filename.split('.')
      let imgType = filename.split('_')
      imgType = imgType[imgType.length - 1]

      if (!valid_types.includes(imgType)) {
        setError(
          `Invalid image type "${imgType}". Must be one of these: ${valid_types}`
        )
        return
      }

      const expectedExt = ['o4', 'o5', 'art800'].includes(imgType)
        ? 'jpg'
        : 'png'

      if (expectedExt !== ext) {
        setError(
          `Expected "${expectedExt}" extension for img ${imgType}, got "${ext}"`
        )
        return
      }

      if (['o4', 'o5'].includes(imgType)) {
        secondBatch.push([f, referencia, imgType])
      } else {
        firstBatch.push([f, referencia, imgType])
      }
    }

    // 2. if the validation passed, now we upload the images
    // We upload images in two batched to prevent API conflicts
    await Promise.all(
      firstBatch.map(([f, ref, imgType]) => {
        setUploading((state) => ({ ...state, [imgType]: true }))
        return uploadImage(f, ref, imgType)
      })
    )

    await Promise.all(
      secondBatch.map(([f, ref, imgType]) => {
        setUploading((state) => ({ ...state, [imgType]: true }))
        return uploadImage(f, ref, imgType)
      })
    )
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', marginRight: 40 }}>
        <ImagePreview
          prod={prod}
          referencia={referencia}
          imgType="anv800"
          path={imgPaths?.[`${referencia}_anv800`]?.path}
          uploading={uploading?.['anv800']}
        />
        <ImagePreview
          prod={prod}
          referencia={referencia}
          imgType="rev800"
          path={imgPaths?.[`${referencia}_rev800`]?.path}
          uploading={uploading?.['rev800']}
        />
        <ImagePreview
          prod={prod}
          referencia={referencia}
          imgType="art800"
          path={imgPaths?.[`${referencia}_art800`]?.path}
          uploading={uploading?.['art800']}
        />
      </div>
      <div
        style={{
          paddingLeft: 40,
          borderLeft: '2px dashed #ddd',
          display: 'flex',
        }}
      >
        <ImagePreview
          prod={prod}
          referencia={referencia}
          imgType="o4"
          path={imgPaths?.[`${referencia}_o4`]?.path}
          uploading={uploading?.['o4']}
        />
        <ImagePreview
          prod={prod}
          referencia={referencia}
          imgType="o5"
          path={imgPaths?.[`${referencia}_o5`]?.path}
          uploading={uploading?.['o5']}
        />
      </div>
      <div>
        {error && <Alert type={'error'} message={error} />}
        <p>Use the following button to upload multiple images at once:</p>
        <MultiFileUpload
          accept="image/*"
          disabled={isUploading}
          onChange={onChange}
          label={isUploading ? 'Uploading...' : 'Upload product photos'}
        />
        <br />
        <p>
          <b>IMPORTANT</b>: Make sure each file name ends with one of these
          identifiers: <br />
          <b>anv800.png</b>, <b>rev800.png</b>, <b>art800.jpg</b>, <b>o4.jpg</b>{' '}
          or <b>o5.jpg</b>
        </p>
      </div>
    </div>
  )
}

function ImagePreview({ prod, referencia, imgType, path, uploading }) {
  const prod_type = prod.tipo.substring(1, 3)
  const folder = ['o4', 'o5'].includes(imgType) ? 'proriginal' : 'prfmtgrande'
  const extension = ['o4', 'o5', 'art800'].includes(imgType) ? 'jpg' : 'png'

  return (
    <div style={{ padding: '0 15', width: 180, textAlign: 'center' }}>
      {uploading && (
        <Spin size="large" style={{ position: 'absolute', marginTop: 125 }} />
      )}
      <div style={{ marginBottom: 10, opacity: uploading ? 0.5 : 1 }}>
        <img
          alt={`${referencia}_${imgType}`}
          style={{ maxHeight: 250 }}
          onError={(e) => {
            e.currentTarget.src =
              prod_type === 'pk' ? fallbackPackImg : fallbackProdImg
          }}
          src={
            path
              ? `${cdn}${path}`
              : `${cdn}${folder}/${prod_type}/${referencia}_${imgType}.${extension}`
          }
        />
      </div>
      <strong>
        {imgType}.{extension}
      </strong>
    </div>
  )
}

export function MultiFileUpload({ label, onChange, ...props }) {
  const changed = async (event) => {
    const files = Array.from(event.target.files)
    await onChange(await Promise.all(files.map((f) => lightFileReader(f))))
  }

  return (
    <div className="file">
      <label className="file-label">
        <input
          className="file-input"
          multiple
          {...props}
          type="file"
          name="file"
          onChange={changed}
        />
        <span className="file-cta">
          <span className="file-icon">
            <i className="fas fa-upload"></i>
          </span>
          <span className="file-label">{label || 'Choose a file…'}</span>
        </span>
      </label>
    </div>
  )
}
